import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import type * as React from "react"
import { useCallback, useContext } from "react"
import { getCarIssuesJid } from "@/jidUtils"
import { XmppContext } from "@/stream/xmppClient"
import { useSidebar } from "./SidebarContext"
import { selectChat, selectChatCounts } from "@/reducers/chatsSliceSelectors"
import { shallowEqual } from "react-redux"
import BaseSidebarButton from "./BaseSidebarButton"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { TriangleAlert } from "lucide-react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { addBookmark } from "@/api/xeps/PepNativeBookmarks"
import { joinChat } from "@/reducers/chatsSliceThunks"

const CAR_ISSUES_ROOM_NAME = "Car Accidents"

const CarIssuesSidebarButton = ({ className }: { className?: string }) => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const { onActiveChatChange } = useSidebar()
  const { activeTab } = useAppSelector(selectTabs)
  const chatCounts = useAppSelector(
    selectChatCounts(myJid.toString()),
    shallowEqual,
  )
  const isCarIssuesEnabled = Boolean(useFeatureFlagEnabled("car-issues"))

  const carIssuesJid = getCarIssuesJid(myJid)
  const carIssuesChat = useAppSelector(selectChat(carIssuesJid))
  const active = activeTab === TAB_TYPES.CAR_ISSUES
  const unread = chatCounts[carIssuesJid] || 0

  const handleClick = useCallback(async () => {
    if (!carIssuesChat) {
      try {
        await addBookmark(client, carIssuesJid, CAR_ISSUES_ROOM_NAME)
        window.analytics.track("AddBookmark", {
          chatJid: carIssuesJid,
          chatName: CAR_ISSUES_ROOM_NAME,
          from: "CarIssuesSidebarButton",
        })
      } catch (e) {
        console.error("Failed to bookmark car issues room", e)
      }
      dispatch(
        joinChat(client, {
          jid: carIssuesJid,
          name: CAR_ISSUES_ROOM_NAME,
          room: true,
        }),
      )
    }
    onActiveChatChange(carIssuesJid)
  }, [carIssuesChat, client, dispatch, onActiveChatChange, carIssuesJid])

  if (!isCarIssuesEnabled) return null

  const tooltipText =
    unread > 0
      ? t("app.sidebar.carIssuesUnread", { unread })
      : t("app.sidebar.carIssues")

  return (
    <BaseSidebarButton
      tabName={TAB_TYPES.CAR_ISSUES}
      icon={TriangleAlert}
      activeIcon={TriangleAlert}
      tooltipText={tooltipText}
      className={className}
      onClick={handleClick}
    >
      {!active && unread > 0 && (
        <div className="absolute right-[-3px] top-[-3px] flex h-4 min-w-4 items-center justify-center rounded-lg bg-base-primary p-1 text-[10px] font-semibold text-white">
          {unread}
        </div>
      )}
    </BaseSidebarButton>
  )
}

export default CarIssuesSidebarButton
